import { useState } from 'react';

import type { CityListingPages } from '@todo/core/city-listings';
import { cities } from '@/lib/events-data';
import { readFooterLinks } from '@/lib/footer-links';
import { CITY_LISTING_PAGES_SEED_KEY, LIST_BUCKET, type FooterCityLinks } from '@todo/core/list-queries';
import { getPrefetched } from '@todo/core/static-prefetch';

const allCityIds = cities.map((c) => c.id);

/**
 * Which cities have an exported /[city]/events and /[city]/tours page — the
 * exact list the export generated pages for, so a link never 404s.
 *
 * A page that links to them bakes the list itself
 * (`listSeedScript(CITY_LISTING_PAGES_SEED_KEY)`), so its hydration render
 * reads back what the export rendered. After a client-side navigation there's
 * no such <script> for the new page, so it falls back to the same list as
 * baked by the site footer, which is on every page (readFooterLinks). With
 * neither — `expo start` and native, where nothing is exported and every
 * route resolves — every city, like the footer and the /boats hub.
 */
export function useCityListingPages(): CityListingPages {
  const [pages] = useState<CityListingPages>(() => {
    const baked = getPrefetched<CityListingPages>(LIST_BUCKET, CITY_LISTING_PAGES_SEED_KEY);
    if (baked) return baked;
    const footer = readFooterLinks();
    if (footer) return { events: footer.cityEvents, tours: footer.cityTours };
    return { events: allCityIds, tours: allCityIds };
  });
  return pages;
}

/**
 * Every kind of exported per-city page (boat rental, wine tours, events,
 * tours), as baked by the site footer — so a link built from it never points
 * at an unexported page. Every city where nothing was exported (`expo start`,
 * native), like the footer itself.
 */
export function useFooterCityLinks(): FooterCityLinks {
  const [pages] = useState<FooterCityLinks>(
    () =>
      readFooterLinks() ?? {
        boatRental: allCityIds,
        wineTours: allCityIds,
        cityEvents: allCityIds,
        cityTours: allCityIds,
      },
  );
  return pages;
}
